import React, { useState, useRef, useEffect } from 'react';
import { useAuthStore } from '../../../stores/useAuthStore';
import './LoginScreen.css';
import Button from '../../ui/Button/Button';

const LOGIN_SUBMIT_DELAY_MS = 300;

const LoginScreen: React.FC = () => {
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { login } = useAuthStore();
    const passwordRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        // Auto-focus password input when component mounts
        if (passwordRef.current) {
            passwordRef.current.focus();
        }
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!password.trim()) {
            setError('Please enter a password');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            await new Promise(resolve => setTimeout(resolve, LOGIN_SUBMIT_DELAY_MS));
            const success = await login(password);

            if (!success) {
                setError('Incorrect password');
                setPassword('');
                if (passwordRef.current) {
                    passwordRef.current.focus();
                }
            }
        } catch (loginError) {
            setError((loginError as Error).message || 'Unable to sign in');
        } finally {
            setIsLoading(false);
        }
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value);
        if (error) {
            setError('');
        }
    };

    return (
        <div className="login-overlay">
            <div className="login-container">
                <div className="login-content">
                    <p className="login-subtitle">Enter password to access memories</p>

                    <form onSubmit={handleSubmit} className="login-form">
                        <div className="login-input-group">
                            <input
                                ref={passwordRef}
                                type="password"
                                value={password}
                                onChange={handlePasswordChange}
                                placeholder="Password"
                                className={`login-input ${error ? 'login-input-error' : ''}`}
                                disabled={isLoading}
                                autoComplete="current-password"
                                autoFocus
                            />
                            {error && (
                                <span className="login-error-message">{error}</span>
                            )}
                        </div>

                        <Button
                            type="submit"
                            variant="primary"
                            size="large"
                            fullWidth
                            className="login-button"
                            disabled={isLoading}
                        >
                            {isLoading ? 'Accessing...' : 'Enter'}
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default LoginScreen;
